import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrDocumentType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

export function DocumentTypeSelect({
  label = 'Document type',
  value = '',
  onChange,
  placeholder,
  className,
  required,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('hr');
  const [types, setTypes] = useState<HrDocumentType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listDocumentTypes()
      .then(setTypes)
      .catch((err) => showApiError(err, t('errors.failedToLoadDocumentTypes')))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      types.map((type) => ({
        value: String(type.id),
        label: type.expiryTracked ? `${type.name} · expiry tracked` : type.name,
      })),
    [types],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectDocumentType}
      loading={loading}
      searchPlaceholder={selectLabels.searchDocumentTypes}
      emptyMessage={selectLabels.noDocumentTypes}
    />
  );
}
